import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";
import ReactDOM from "react-dom";
import { pdf } from "@react-pdf/renderer";
import toast from "react-hot-toast";
import {
  X,
  Save,
  Send,
  CheckCircle2,
  Download,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Loader2,
  FileText,
  Clock,
  History,
  AlertTriangle,
  Plus,
  Trash2
} from "lucide-react";
import NdaSignaturePad, { SignaturePadHandle } from "./NdaSignaturePad";
import { SowPdfDocument } from "./SowPdfDocument";
import type { SowModule } from "./SowPdfDocument";
import {
  saveSowDraft,
  submitSowForReview,
  approveSow,
  reopenSow,
  emptySowFields
} from "../../utils/api/endpoints/sow";
import type {
  SowFields,
  SowTargetItem,
  SowStatus,
  SowBlob,
  SowHistoryEntry
} from "../../utils/api/endpoints/sow";
import { SalesButton } from "../sales/sales-ui";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SowBuilderModalProps = {
  responseId: string;
  sowStatus: SowStatus | null;
  sowVersion: number;
  sowBlob: SowBlob | null;
  clientName: string;
  modules: SowModule[];
  initialPrice: number | null;
  initialCurrency: "NZD" | "USD";
  initialConsultantDays: number | null;
  clientEmail?: string;
  clientPhone?: string;
  clientSignerName?: string;
  clientSignerTitle?: string;
  clientRegisteredCompany?: string;
  clientCountryOfIncorporation?: string;
  clientBillingName?: string;
  clientBillingEmail?: string;
  clientBillingPhone?: string;
  clientBillingAddress?: string;
  clientTaxNumber?: string;
  clientEmergencyContact?: string;
  preferredStartDate?: string;
  ctbEngagementLead?: string;
  ctbSignerName?: string;
  ctbSignerTitle?: string;
  onClose: () => void;
  onUpdate: (status: SowStatus | null, version: number, blob: SowBlob | null) => void;
};

// ─── Accordion section ────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({
  title,
  badge,
  badgeColor = "bg-blue-50 text-blue-700 border-blue-200",
  children,
  defaultOpen = false
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition text-left"
      >
        <span className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-700">
            {title}
          </span>
          {badge && (
            <span
              className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${badgeColor}`}
            >
              {badge}
            </span>
          )}
        </span>
        {open ? (
          <ChevronDown size={13} className="text-slate-400 shrink-0" />
        ) : (
          <ChevronRight size={13} className="text-slate-400 shrink-0" />
        )}
      </button>
      {open && <div className="p-3 space-y-2.5 bg-white">{children}</div>}
    </div>
  );
};

// ─── Field primitives ─────────────────────────────────────────────────────────

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required
}) => (
  <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
    {children}
    {required && <span className="text-rose-500 ml-0.5">*</span>}
  </label>
);

const TextInput: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { label?: string }
> = ({ label, className = "", ...props }) => (
  <div>
    {label && <Label>{label}</Label>}
    <input
      className={`w-full h-7 px-2.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition disabled:bg-slate-50 disabled:text-slate-400 ${className}`}
      {...props}
    />
  </div>
);

const Textarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; rows?: number }
> = ({ label, rows = 3, className = "", ...props }) => (
  <div>
    {label && <Label>{label}</Label>}
    <textarea
      rows={rows}
      className={`w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition resize-y disabled:bg-slate-50 disabled:text-slate-400 ${className}`}
      {...props}
    />
  </div>
);

const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
  <div>
    <Label>{label}</Label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full h-7 px-2.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition disabled:bg-slate-50 disabled:text-slate-400"
    />
  </div>
);

const SelectInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ label, value, onChange, disabled, children }) => (
  <div>
    <Label>{label}</Label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full h-7 px-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition disabled:bg-slate-50 disabled:text-slate-400"
    >
      {children}
    </select>
  </div>
);

// ─── Status badge ─────────────────────────────────────────────────────────────

const sowStatusStyles: Record<string, string> = {
  DRAFT: "bg-amber-50 text-amber-800 border-amber-200",
  IN_REVIEW: "bg-blue-50 text-blue-800 border-blue-200",
  FINAL: "bg-emerald-50 text-emerald-800 border-emerald-200"
};

const SowStatusBadge: React.FC<{ status: SowStatus | null }> = ({ status }) => {
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${
        sowStatusStyles[status] ?? "bg-slate-50 text-slate-600 border-slate-200"
      }`}
    >
      {status.replace("_", " ")}
    </span>
  );
};

// ─── CTB Signature Pad ────────────────────────────────────────────────────────

const CtbSignaturePad: React.FC<{
  value: string | null;
  onChange: (base64: string | null) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const sigRef = useRef<SignaturePadHandle>(null);
  const [mode, setMode] = useState<"draw" | "preview">(value ? "preview" : "draw");

  const handleClear = () => {
    sigRef.current?.clear();
    onChange(null);
    setMode("draw");
  };

  const handleSave = () => {
    if (sigRef.current?.isEmpty()) {
      toast.error("Please draw a signature first.");
      return;
    }
    const data = sigRef.current!.toDataURL("image/png");
    onChange(data);
    setMode("preview");
    toast.success("CTB signature captured.");
  };

  if (disabled && !value) return null;

  return (
    <div className="space-y-2">
      <Label>CTB Authorised Signature (optional)</Label>
      <p className="text-[10px] text-slate-400 leading-relaxed">
        Draw the CTB signature to embed it in the PDF. Leave blank to sign externally.
      </p>
      {mode === "preview" && value ? (
        <div className="flex items-start gap-3">
          <div className="border border-slate-200 rounded-lg p-2 bg-white">
            <img src={value} alt="CTB signature" className="h-12 object-contain" />
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-[10px] text-rose-600 underline hover:text-rose-700 mt-2"
            >
              Clear
            </button>
          )}
        </div>
      ) : !disabled ? (
        <div className="space-y-2">
          <div className="border border-dashed border-slate-300 rounded-lg bg-slate-50 overflow-hidden">
            <NdaSignaturePad ref={sigRef} className="w-full h-20" />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-100 transition"
            >
              Use this signature
            </button>
            <button
              type="button"
              onClick={() => sigRef.current?.clear()}
              className="text-[10px] text-slate-500 hover:text-slate-700 underline"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

// ─── Helpers for Date Calculations ──────────────────────────────────────────

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addWorkingDays(startDate: Date, days: number): Date {
  const cur = new Date(startDate.getTime());
  let added = 0;
  while (added < days) {
    cur.setDate(cur.getDate() + 1);
    const dayOfWeek = cur.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return cur;
}

function addCalendarDays(startDate: Date, days: number): Date {
  const cur = new Date(startDate.getTime());
  cur.setDate(cur.getDate() + days);
  return cur;
}

function getNextBusinessMonday(): Date {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day; // next Monday
  d.setDate(d.getDate() + diff);
  return d;
}

// ─── Main component ───────────────────────────────────────────────────────────

export const SowBuilderModal: React.FC<SowBuilderModalProps> = ({
  responseId,
  sowStatus: initialSowStatus,
  sowVersion: initialSowVersion,
  sowBlob: initialSowBlob,
  clientName,
  modules,
  initialPrice,
  initialCurrency,
  initialConsultantDays,
  clientEmail,
  clientPhone,
  clientSignerName,
  clientSignerTitle,
  clientRegisteredCompany,
  clientCountryOfIncorporation,
  clientBillingName,
  clientBillingEmail,
  clientBillingPhone,
  clientBillingAddress,
  clientTaxNumber,
  clientEmergencyContact,
  preferredStartDate,
  ctbEngagementLead,
  ctbSignerName,
  ctbSignerTitle,
  onClose,
  onUpdate
}) => {
  // ── Local copies of SoW state (so modal can update independently) ──────────
  const [sowStatus, setSowStatus] = useState(initialSowStatus);
  const [sowVersion, setSowVersion] = useState(initialSowVersion);
  const [sowBlob, setSowBlob] = useState(initialSowBlob);

  // ── Fields state ──────────────────────────────────────────────────────────
  const [fields, setFields] = useState<SowFields>(() => {
    const def = emptySowFields();
    def.price = initialPrice;
    def.currency = initialCurrency;
    def.consultantDays = initialConsultantDays;

    const clientDisplay = clientName && clientName.trim() ? clientName.trim() : "Client";

    // Personalized Section 1 & 2
    def.introductionText = `This Statement of Work (“SOW”) is entered into by and between Central Test Bed Limited (“CTB”) and ${clientDisplay} (“Client”), and defines the scope of work, technical objectives, and terms under which CTB will deliver professional security assessment and penetration testing services.`;
    def.objectiveText = `The primary objective of this engagement is to assess the security posture of ${clientDisplay}’s designated target environments, identify vulnerabilities, validate exploitable attack vectors, evaluate business impact, and deliver actionable remediation guidance to strengthen ${clientDisplay}’s defensive baseline.`;

    // Section 3: Scope Boundary
    def.scopeBoundaryText = `Testing is strictly limited to the targets and assets explicitly listed in Table 1 above. Any third-party infrastructure, external service providers, downstream payment processors, or unlisted domains and IP addresses are strictly out of scope unless explicitly authorised in writing.`;

    // Section 5: Smart Timeline & Working Day Date Calculations
    const parsedStart = preferredStartDate ? new Date(preferredStartDate) : null;
    const validStart = parsedStart && !isNaN(parsedStart.getTime()) ? parsedStart : getNextBusinessMonday();
    const days = initialConsultantDays && initialConsultantDays > 0 ? initialConsultantDays : 5;

    def.testingStartDate = toIsoDate(validStart);
    const fieldworkEnd = addWorkingDays(validStart, Math.max(1, days - 1));
    def.testingEndDate = toIsoDate(fieldworkEnd);
    const draftReport = addWorkingDays(fieldworkEnd, 2);
    def.draftReportDate = toIsoDate(draftReport);
    const finalReport = addWorkingDays(draftReport, 5);
    def.finalReportDate = toIsoDate(finalReport);
    const retestEnd = addCalendarDays(finalReport, 30);
    def.retestingEndDate = toIsoDate(retestEnd);

    def.indicativeDeliveryDuration = `${days} Business Days (Fieldwork)`;
    def.baselineAssessmentDuration = `${days} Days`;
    def.timelineAssumptionsText = `Fieldwork is estimated at ${days} business days based on the agreed scoping asset inventory. Retesting of remediated findings is available for up to 90 days following completion of fieldwork.`;

    // Auto-populate client and billing contact details from intake / scoping session
    if (clientSignerName) def.clientSignerName = clientSignerName;
    else if (clientName) def.clientSignerName = clientName;
    if (clientSignerTitle) def.clientSignerTitle = clientSignerTitle;
    if (clientEmail) def.clientEmail = clientEmail;
    if (clientPhone) def.clientPhone = clientPhone;
    if (clientRegisteredCompany) def.clientRegisteredCompany = clientRegisteredCompany;
    else if (clientName) def.clientRegisteredCompany = clientName;
    if (clientCountryOfIncorporation) def.clientCountryOfIncorporation = clientCountryOfIncorporation;
    if (clientBillingName) def.clientBillingName = clientBillingName;
    else if (clientName) def.clientBillingName = clientName;
    if (clientBillingEmail) def.clientBillingEmail = clientBillingEmail;
    else if (clientEmail) def.clientBillingEmail = clientEmail;
    if (clientBillingPhone) def.clientBillingPhone = clientBillingPhone;
    else if (clientPhone) def.clientBillingPhone = clientPhone;
    if (clientBillingAddress) def.clientBillingAddress = clientBillingAddress;
    if (clientTaxNumber) def.clientTaxNumber = clientTaxNumber;

    // Smart Methodology, Coverage & Dependencies mapping based on scoped targets
    const hasWeb = (modules || []).some((m) => /web/i.test(m.assessmentType));
    const hasApi = (modules || []).some((m) => /api/i.test(m.assessmentType));
    const hasNetwork = (modules || []).some((m) => /network|cloud|infra|vpn/i.test(m.assessmentType));
    const hasPayment = (modules || []).some((m) =>
      /payment|checkout|stripe|billing|fintech|gateway|transaction/i.test(`${m.assetName} ${(m.targets || []).join(" ")}`)
    );
    const hasGraybox = (modules || []).some((m) => m.isGraybox);

    def.coverageWebText = hasWeb
      ? "Evaluation of authentication, authorization, session management, input validation, access controls, business logic flaws, injection vulnerabilities, and OWASP Top 10 security standards across web applications."
      : "Web application penetration testing is not in scope for this engagement.";

    def.coverageApiText = hasApi
      ? "Assessment of RESTful / GraphQL API endpoints, authentication tokens, rate limiting, BOLA/BFLA, data exposure, parameter tampering, and OWASP API Top 10 vulnerabilities."
      : "API security testing is not in scope for this engagement.";

    def.coveragePaymentText = hasPayment
      ? "Review of transaction workflows, checkout mechanisms, price/parameter tampering, webhook verification, and third-party payment gateway integration integrity."
      : "Payment gateway workflows and payment processing components are not in scope for this engagement.";

    def.coverageNetworkText = hasNetwork
      ? "External/internal perimeter discovery, port scanning, service enumeration, protocol analysis, TLS/SSL configuration, and host-level vulnerability identification."
      : "Network infrastructure and host-level penetration testing is not in scope for this engagement.";

    def.clientDependencies = hasGraybox
      ? "1. Provisioning of 2x standard user accounts and 1x administrative account per scoped target at least 2 business days prior to testing.\n2. Testing source IPs allowlisted across client WAF, IDS/IPS, cloud perimeter, and rate-limiting controls.\n3. Dedicated technical point of contact available during testing hours for incident response and credential verification.\n4. Timely notification to cloud/hosting providers where required under their acceptable use policies."
      : "1. Testing source IPs allowlisted across client WAF, IDS/IPS, cloud perimeter, and rate-limiting controls.\n2. Dedicated technical point of contact available during testing hours.\n3. Timely notification to cloud/hosting providers where required under their acceptable use policies.";

    // Appendix A – Rules of Engagement defaults
    const envList = Array.from(new Set((modules || []).map((m) => m.environment).filter(Boolean)));
    if (envList.length > 0) {
      def.testingEnvironment = envList.join(", ");
    }
    if (clientEmergencyContact) {
      def.clientEmergencyContact = clientEmergencyContact;
    } else if (clientPhone) {
      def.clientEmergencyContact = `${clientSignerName || clientDisplay} (${clientPhone})`;
    }
    if (ctbEngagementLead) {
      def.ctbEngagementLead = ctbEngagementLead;
    }
    if (ctbSignerName) {
      def.ctbSignerName = ctbSignerName;
    }
    if (ctbSignerTitle) {
      def.ctbSignerTitle = ctbSignerTitle;
    }
    def.testAccountsNote = hasGraybox
      ? "Gray-box testing: Client will provide 2x non-privileged user accounts and 1x administrative account per scoped target to evaluate role segregation and privilege escalation."
      : "Black-box testing: No test accounts required.";

    // Auto-populate targets from modules
    if (modules && modules.length > 0) {
      def.targetItems = modules.map((mod, idx) => ({
        id: `tgt-${idx + 1}`,
        assetName: mod.assetName || "",
        assessmentType: mod.assessmentType || "WEB_APP",
        accessType: mod.isGraybox ? "graybox" : "blackbox",
        targets: Array.isArray(mod.targets) ? mod.targets.join(", ") : (mod.targets || ""),
        environment: mod.environment || "Production",
        status: mod.status || "Confirmed",
        description: mod.description || `Assessment of ${mod.assetName || "the specified target"} across identified endpoints and business workflows.`
      }));
      def.authorisedTargets = def.targetItems
        .map((t) => `• ${t.assetName} (${t.assessmentType.replace(/_/g, " ")}, ${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
        .join("\n");
      if (def.targetItems[0]?.environment) {
        def.testingEnvironment = def.targetItems[0].environment;
      }
    } else {
      def.targetItems = [{
        id: `tgt-1`,
        assetName: "",
        assessmentType: "WEB_APP",
        accessType: "graybox",
        targets: "",
        environment: "Production",
        status: "Confirmed",
        description: ""
      }];
    }

    // If an existing saved blob exists, merge it cleanly while preserving auto-populated fields where saved has empty/blank values
    if (initialSowBlob?.fields) {
      const saved = initialSowBlob.fields;
      const savedHasTargets = saved.targetItems && saved.targetItems.some((t: any) =>
        Boolean((t.assetName && t.assetName.trim()) || (t.targets && t.targets.trim()))
      );

      const merged: any = { ...def, ...saved };
      (Object.keys(def) as (keyof SowFields)[]).forEach((key) => {
        const val = saved[key];
        if (val === undefined || val === null || (typeof val === "string" && !val.trim())) {
          merged[key] = def[key];
        }
      });
      if (!savedHasTargets) {
        merged.targetItems = def.targetItems;
        merged.authorisedTargets = def.authorisedTargets;
      }

      return merged as SowFields;
    }

    return def;
  });

  const set = useCallback(
    <K extends keyof SowFields>(key: K, value: SowFields[K]) =>
      setFields((prev) => {
        const next = { ...prev, [key]: value };

        if (key === "consultantDays") {
          const days = typeof value === "number" && value > 0 ? value : null;
          if (days !== null) {
            next.indicativeDeliveryDuration = `${days} Business Days (Fieldwork)`;
            next.baselineAssessmentDuration = `${days} ${days === 1 ? "Day" : "Days"}`;

            if (prev.testingStartDate) {
              const start = new Date(prev.testingStartDate);
              if (!isNaN(start.getTime())) {
                const fieldworkEnd = addWorkingDays(start, Math.max(1, days - 1));
                const draftReport = addWorkingDays(fieldworkEnd, 2);
                const finalReport = addWorkingDays(draftReport, 5);
                const retestEnd = addCalendarDays(finalReport, 30);

                next.testingEndDate = toIsoDate(fieldworkEnd);
                next.draftReportDate = toIsoDate(draftReport);
                next.finalReportDate = toIsoDate(finalReport);
                next.retestingEndDate = toIsoDate(retestEnd);
              }
            }

            if (prev.timelineAssumptionsText) {
              next.timelineAssumptionsText = `Fieldwork is estimated at ${days} business days based on the agreed scoping asset inventory. Retesting of remediated findings is available for up to 90 days following completion of fieldwork.`;
            }
          }
        } else if (key === "indicativeDeliveryDuration") {
          if (typeof value === "string" && value) {
            const match = value.match(/^(\d+(?:\.\d+)?)/);
            if (match) {
              const days = parseFloat(match[1]);
              if (!isNaN(days) && days > 0) {
                next.consultantDays = days;
                next.baselineAssessmentDuration = `${days} ${days === 1 ? "Day" : "Days"}`;

                if (prev.testingStartDate) {
                  const start = new Date(prev.testingStartDate);
                  if (!isNaN(start.getTime())) {
                    const fieldworkEnd = addWorkingDays(start, Math.max(1, days - 1));
                    const draftReport = addWorkingDays(fieldworkEnd, 2);
                    const finalReport = addWorkingDays(draftReport, 5);
                    const retestEnd = addCalendarDays(finalReport, 30);

                    next.testingEndDate = toIsoDate(fieldworkEnd);
                    next.draftReportDate = toIsoDate(draftReport);
                    next.finalReportDate = toIsoDate(finalReport);
                    next.retestingEndDate = toIsoDate(retestEnd);
                  }
                }

                if (prev.timelineAssumptionsText) {
                  next.timelineAssumptionsText = `Fieldwork is estimated at ${days} business days based on the agreed scoping asset inventory. Retesting of remediated findings is available for up to 90 days following completion of fieldwork.`;
                }
              }
            }
          }
        } else if (key === "testingStartDate") {
          if (typeof value === "string" && value) {
            const start = new Date(value);
            if (!isNaN(start.getTime())) {
              const days = prev.consultantDays && prev.consultantDays > 0 ? prev.consultantDays : 5;
              const fieldworkEnd = addWorkingDays(start, Math.max(1, days - 1));
              const draftReport = addWorkingDays(fieldworkEnd, 2);
              const finalReport = addWorkingDays(draftReport, 5);
              const retestEnd = addCalendarDays(finalReport, 30);

              next.testingEndDate = toIsoDate(fieldworkEnd);
              next.draftReportDate = toIsoDate(draftReport);
              next.finalReportDate = toIsoDate(finalReport);
              next.retestingEndDate = toIsoDate(retestEnd);
            }
          }
        }

        return next;
      }),
    []
  );

  const isReadonly = sowStatus === "FINAL";
  const canEdit = !sowStatus || sowStatus === "DRAFT";
  const canSubmit = !sowStatus || sowStatus === "DRAFT";
  const canApprove = sowStatus === "IN_REVIEW";
  const canReopen = sowStatus === "IN_REVIEW" || sowStatus === "FINAL";

  // ── PDF Preview state ─────────────────────────────────────────────────────
  const [selectedVersionEntry, setSelectedVersionEntry] = useState<SowHistoryEntry | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBuilding, setPdfBuilding] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Track in-progress build so we can cancel on fields change
  const buildAbortRef = useRef(false);
  // Track last built fields so we can detect changes
  const lastBuiltFieldsRef = useRef<string>("");

  const buildPdf = useCallback(
    async (f: SowFields, versionOverride?: number) => {
      // Prevent concurrent builds
      buildAbortRef.current = true;
      await new Promise((r) => setTimeout(r, 10));
      buildAbortRef.current = false;

      setPdfBuilding(true);
      setPdfError(null);

      const targetVersion = versionOverride ?? (selectedVersionEntry ? selectedVersionEntry.version : (sowVersion + (sowStatus ? 0 : 1)));

      try {
        const blob = await pdf(
          <SowPdfDocument
            fields={f}
            clientName={clientName || "Client"}
            sowVersion={targetVersion}
            modules={modules || []}
          />
        ).toBlob();

        if (buildAbortRef.current) return; // superseded by a newer build

        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        lastBuiltFieldsRef.current = JSON.stringify(f);
      } catch (err: any) {
        console.error("PDF generation error:", err);
        if (!buildAbortRef.current) {
          setPdfError(err?.message || "Failed to render PDF document.");
        }
      } finally {
        if (!buildAbortRef.current) setPdfBuilding(false);
      }
    },
    [clientName, sowVersion, sowStatus, modules]
  );

  // Auto-build on mount
  useEffect(() => {
    buildPdf(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-rebuild with 1.5s debounce on fields change
  useEffect(() => {
    // Skip if fields haven't actually changed
    if (JSON.stringify(fields) === lastBuiltFieldsRef.current) return;

    const timer = setTimeout(() => buildPdf(fields), 1500);
    return () => clearTimeout(timer);
  }, [fields, buildPdf]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      buildAbortRef.current = true;
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Keyboard: Escape to close ─────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // ── API actions ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reopening, setReopening] = useState(false);

  const propagate = (status: SowStatus | null, version: number, blob: SowBlob | null) => {
    setSowStatus(status);
    setSowVersion(version);
    setSowBlob(blob);
    onUpdate(status, version, blob);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    const tid = toast.loading("Saving SoW draft…");
    try {
      const res = await saveSowDraft(responseId, fields);
      toast.success("Draft saved", { id: tid });
      propagate(res.sow_status, res.sow_version, res.sow_data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to save draft", { id: tid });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSubmitting(true);
    const tid = toast.loading("Saving and submitting for review…");
    try {
      const saved = await saveSowDraft(responseId, fields);
      const submitted = await submitSowForReview(responseId);
      toast.success("SoW submitted for review", { id: tid });
      propagate(submitted.sow_status, saved.sow_version, saved.sow_data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to submit", { id: tid });
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    const tid = toast.loading("Approving SoW…");
    try {
      const res = await approveSow(responseId);
      toast.success("SoW approved — FINAL", { id: tid });
      propagate(res.sow_status, sowVersion, sowBlob);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to approve", { id: tid });
    } finally {
      setApproving(false);
    }
  };

  const handleReopen = async () => {
    setReopening(true);
    const tid = toast.loading("Reopening SoW…");
    try {
      const res = await reopenSow(responseId);
      const nextVer = res.sow_version || sowVersion + 1;
      toast.success(`SoW reopened for editing (v${nextVer})`, { id: tid });
      propagate(res.sow_status, nextVer, res.sow_data || sowBlob);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to reopen", { id: tid });
    } finally {
      setReopening(false);
    }
  };

  const handleDownload = async () => {
    const tid = toast.loading("Generating PDF…");
    try {
      const activeFields = selectedVersionEntry ? selectedVersionEntry.fields : fields;
      const activeVer = selectedVersionEntry ? selectedVersionEntry.version : sowVersion;
      const blob = await pdf(
        <SowPdfDocument
          fields={activeFields}
          clientName={clientName || "Client"}
          sowVersion={activeVer}
          modules={modules}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CTB_SoW_${(clientName || "Client").replace(/\s+/g, "_")}_v${activeVer}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded", { id: tid });
    } catch {
      toast.error("Failed to generate PDF", { id: tid });
    }
  };

  // ── History panel toggle ──────────────────────────────────────────────────
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Render ─────────────────────────────────────────────────────────────────
  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-stretch">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal panel — full height, very wide */}
      <div className="relative z-10 flex flex-col w-full max-w-[1440px] mx-auto my-4 bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* ── Header ── */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-gradient-to-r from-[#0f2d7a]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-[#0f2d7a]/10 flex items-center justify-center">
              <FileText size={13} className="text-[#0f2d7a]" />
            </div>
            <div>
              <h2 className="text-xs font-bold text-slate-900 leading-none">
                Statement of Work Builder
              </h2>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                {clientName}
                {selectedVersionEntry ? (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                      <Clock size={10} className="text-amber-700" />
                      v{selectedVersionEntry.version} (Viewing Old Version)
                    </span>
                  </>
                ) : sowStatus ? (
                  <>
                    <span className="text-slate-300">·</span>
                    <SowStatusBadge status={sowStatus} />
                    <span className="text-slate-300">·</span>
                    <span>v{sowVersion}</span>
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!selectedVersionEntry && (
              <>
                {/* Download always available */}
                <SalesButton variant="secondary" onClick={handleDownload} disabled={pdfBuilding}>
                  <Download size={11} />
                  Download PDF
                </SalesButton>
                {/* Reopen */}
                {canReopen && (
                  <SalesButton variant="secondary" onClick={handleReopen} disabled={reopening}>
                    <RefreshCw size={11} className={reopening ? "animate-spin" : ""} />
                    {reopening ? "Reopening…" : "Reopen"}
                  </SalesButton>
                )}
                {/* Approve */}
                {canApprove && (
                  <SalesButton variant="success" onClick={handleApprove} disabled={approving}>
                    <CheckCircle2 size={11} />
                    {approving ? "Approving…" : "Approve & Finalise"}
                  </SalesButton>
                )}
                {/* Save Draft */}
                {canEdit && (
                  <SalesButton variant="secondary" onClick={handleSaveDraft} disabled={saving}>
                    <Save size={11} />
                    {saving ? "Saving…" : "Save Draft"}
                  </SalesButton>
                )}
                {/* Submit */}
                {canSubmit && (
                  <SalesButton
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitting || saving}
                  >
                    <Send size={11} />
                    {submitting ? "Submitting…" : "Submit for Review"}
                  </SalesButton>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="ml-1 w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* ── Status banners ── */}
        {selectedVersionEntry ? (
          <div className="shrink-0 mx-5 mt-3 flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-[10px] text-amber-900 font-medium">
            <div className="flex items-center gap-2">
              <Clock size={12} className="text-amber-600 shrink-0" />
              <span>
                Viewing Historical Version <strong>v{selectedVersionEntry.version}</strong> (Saved {new Date(selectedVersionEntry.savedAt).toLocaleString()}). Read-only mode — editor is hidden.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setSelectedVersionEntry(null)}
              className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg text-[10px] transition cursor-pointer shrink-0"
            >
              Return to Active Version (v{sowVersion})
            </button>
          </div>
        ) : (
          <>
            {sowStatus === "IN_REVIEW" && (
              <div className="shrink-0 mx-5 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-blue-50 border border-blue-200 text-[10px] text-blue-800 font-medium">
                <Clock size={12} className="text-blue-600 shrink-0" />
                This SoW is <strong>In Review</strong>. Any Solutions Specialist can approve it. The form is locked — use Reopen to make changes.
              </div>
            )}
            {sowStatus === "FINAL" && (
              <div className="shrink-0 mx-5 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-800 font-medium">
                <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                This SoW is <strong>FINAL</strong>. The form is locked. Use Reopen to edit or Download PDF for client delivery.
              </div>
            )}
          </>
        )}

        {/* ── Side-by-side body ── */}
        <div className="flex-1 flex overflow-hidden min-h-0">

          {/* LEFT: Edit Form */}
          <div className="w-[430px] shrink-0 flex flex-col border-r border-slate-100 overflow-hidden">
            <div className="shrink-0 px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Edit Fields
              </span>
              {isReadonly && (
                <span className="text-[9px] text-slate-400 font-medium">Read-only (SoW is {sowStatus})</span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">

              {/* § 1 & 2 — Introduction & Objective */}
              <Section title="§ 1 & 2 · Introduction & Objective" defaultOpen={false}>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-2">
                  Edit introduction and engagement objective paragraphs. Leave blank to use standard template wording.
                </p>
                <Textarea
                  label="Introduction Text (Paragraphs separated by blank lines)"
                  value={fields.introductionText || ""}
                  onChange={(e) => set("introductionText", e.target.value)}
                  rows={3}
                  placeholder="Capture The Bug (CTB) will conduct a penetration test for Client, covering the agreed scope, testing approach, deliverables, responsibilities, and commercial terms…"
                  disabled={isReadonly}
                />
                <Textarea
                  label="Objective of Engagement (Paragraphs separated by blank lines)"
                  value={fields.objectiveText || ""}
                  onChange={(e) => set("objectiveText", e.target.value)}
                  rows={3}
                  placeholder="The objective is to identify and validate material vulnerabilities within Client's authorised targets as defined in this Statement of Work…"
                  disabled={isReadonly}
                />
              </Section>

              {/* § 3 — Authorised Targets */}
              <Section
                title="§ 3 · Scope of Engagement & Targets"
                badge="Auto-filled"
                badgeColor="bg-emerald-50 text-emerald-700 border-emerald-200"
                defaultOpen={true}
              >
                <p className="text-[10px] text-slate-400 leading-relaxed mb-2">
                  Pre-populated from intake scope data. Add, edit, or remove target rows below — changes sync live to the document.
                </p>

                {/* Target Rows list */}
                <div className="space-y-3">
                  {(fields.targetItems || []).map((item, index) => (
                    <div key={item.id || index} className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">
                          Target Row #{index + 1}
                        </span>
                        {!isReadonly && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (fields.targetItems || []).filter((_, i) => i !== index);
                              const syncText = updated
                                .map((t) => `• ${t.assetName} (${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
                                .join("\n");
                              setFields((prev) => ({ ...prev, targetItems: updated, authorisedTargets: syncText }));
                            }}
                            className="text-rose-500 hover:text-rose-700 p-0.5"
                            title="Delete Target Row"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <TextInput
                          label="Asset Name"
                          value={item.assetName}
                          disabled={isReadonly}
                          onChange={(e) => {
                            const updated = [...(fields.targetItems || [])];
                            updated[index] = { ...updated[index], assetName: e.target.value };
                            const syncText = updated
                              .map((t) => `• ${t.assetName} (${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
                              .join("\n");
                            setFields((prev) => ({ ...prev, targetItems: updated, authorisedTargets: syncText }));
                          }}
                        />
                        <SelectInput
                          label="Assessment Type"
                          value={item.assessmentType}
                          disabled={isReadonly}
                          onChange={(v) => {
                            const updated = [...(fields.targetItems || [])];
                            updated[index] = { ...updated[index], assessmentType: v };
                            const syncText = updated
                              .map((t) => `• ${t.assetName} (${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
                              .join("\n");
                            setFields((prev) => ({ ...prev, targetItems: updated, authorisedTargets: syncText }));
                          }}
                        >
                          <option value="WEB_APP">Web Application</option>
                          <option value="API">API / Integration Service</option>
                          <option value="MOBILE_APP">Mobile Application</option>
                          <option value="EXTERNAL_NETWORK">External Network</option>
                          <option value="INTERNAL_NETWORK">Internal Network</option>
                          <option value="CLOUD_CONFIG">Cloud Configuration Audit</option>
                          <option value="CLOUD_PENTEST">Cloud Infrastructure Pentest</option>
                          <option value="AI_LLM">AI & LLM Assessment</option>
                          <option value="CODE_REVIEW">Secure Code Review</option>
                          <option value="M365_REVIEW">M365 Tenant Audit</option>
                          <option value="VPN_FIREWALL_REVIEW">VPN & Firewall Config Review</option>
                          <option value="PENETRATION_TESTING">Penetration Testing</option>
                        </SelectInput>
                        <SelectInput
                          label="Access Level"
                          value={item.accessType}
                          disabled={isReadonly}
                          onChange={(v) => {
                            const updated = [...(fields.targetItems || [])];
                            updated[index] = { ...updated[index], accessType: v as any };
                            const syncText = updated
                              .map((t) => `• ${t.assetName} (${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
                              .join("\n");
                            setFields((prev) => ({ ...prev, targetItems: updated, authorisedTargets: syncText }));
                          }}
                        >
                          <option value="graybox">Graybox</option>
                          <option value="blackbox">Blackbox</option>
                          <option value="whitebox">Whitebox</option>
                        </SelectInput>
                        <TextInput
                          label="Environment"
                          value={item.environment || "Production"}
                          disabled={isReadonly}
                          placeholder="Production"
                          onChange={(e) => {
                            const updated = [...(fields.targetItems || [])];
                            updated[index] = { ...updated[index], environment: e.target.value };
                            setFields((prev) => ({ ...prev, targetItems: updated }));
                          }}
                        />
                        <TextInput
                          label="Status"
                          value={item.status || "Confirmed"}
                          disabled={isReadonly}
                          placeholder="Confirmed"
                          onChange={(e) => {
                            const updated = [...(fields.targetItems || [])];
                            updated[index] = { ...updated[index], status: e.target.value };
                            setFields((prev) => ({ ...prev, targetItems: updated }));
                          }}
                        />
                      </div>
                      <TextInput
                        label="Targets (Domains / IPs / URLs)"
                        value={item.targets}
                        disabled={isReadonly}
                        placeholder="e.g. app.example.com, 192.0.2.1"
                        onChange={(e) => {
                          const updated = [...(fields.targetItems || [])];
                          updated[index] = { ...updated[index], targets: e.target.value };
                          const syncText = updated
                            .map((t) => `• ${t.assetName} (${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
                            .join("\n");
                          setFields((prev) => ({ ...prev, targetItems: updated, authorisedTargets: syncText }));
                        }}
                      />
                      <Textarea
                        label="Asset Description (PDF scope section)"
                        value={item.description || ""}
                        disabled={isReadonly}
                        rows={2}
                        placeholder="e.g. Comprehensive penetration testing of the client's web application and its authenticated functionality…"
                        onChange={(e) => {
                          const updated = [...(fields.targetItems || [])];
                          updated[index] = { ...updated[index], description: e.target.value };
                          setFields((prev) => ({ ...prev, targetItems: updated }));
                        }}
                      />
                    </div>
                  ))}

                  {!isReadonly && (
                    <button
                      type="button"
                      onClick={() => {
                        const newItem: SowTargetItem = {
                          id: `tgt-${Date.now()}`,
                          assetName: "",
                          assessmentType: "WEB_APP",
                          accessType: "graybox",
                          targets: "",
                          environment: "Production",
                          status: "Confirmed",
                          description: ""
                        };
                        const updated = [...(fields.targetItems || []), newItem];
                        const syncText = updated
                          .map((t) => `• ${t.assetName} (${t.accessType.toUpperCase()}): ${t.targets} [${t.environment || "Production"}]`)
                          .join("\n");
                        setFields((prev) => ({ ...prev, targetItems: updated, authorisedTargets: syncText }));
                      }}
                      className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1 transition"
                    >
                      <Plus size={12} />
                      Add Target Row
                    </button>
                  )}
                </div>

                <div className="mt-2 pt-2 border-t border-slate-150">
                  <Textarea
                    label="Scope Boundary and Endpoint Discovery text"
                    value={fields.scopeBoundaryText || ""}
                    onChange={(e) => set("scopeBoundaryText", e.target.value)}
                    rows={2}
                    placeholder="Testing is limited to the formally authorised targets confirmed by Client before testing begins…"
                    disabled={isReadonly}
                  />
                </div>

                <div className="mt-2 pt-2 border-t border-slate-150">
                  <Label>Combined Summary Output</Label>
                  <Textarea
                    value={fields.authorisedTargets}
                    onChange={(e) => set("authorisedTargets", e.target.value)}
                    rows={3}
                    placeholder="e.g. app.example.com (Web App, Graybox): *.app.example.com [Production]"
                    disabled={isReadonly}
                  />
                </div>
              </Section>

              {/* § 4 — Testing Methodology & Lifecycle */}
              <Section title="§ 4 · Methodology & Execution Lifecycle" defaultOpen={false}>
                <p className="text-[10px] text-slate-400 mb-2">Edit methodology statement and execution lifecycle phases.</p>
                <Textarea
                  label="Methodology Overview Text"
                  value={fields.testingMethodologyText || ""}
                  onChange={(e) => set("testingMethodologyText", e.target.value)}
                  rows={3}
                  placeholder="CTB will perform the assessment in alignment with recognised penetration testing guidance (PTES, NIST, OWASP)…"
                  disabled={isReadonly}
                />

                <div className="space-y-2 mt-2">
                  <Label>Execution Lifecycle Phases</Label>
                  {(fields.executionLifecycle || []).map((row, idx) => (
                    <div key={idx} className="p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold text-slate-500 uppercase">Phase {idx + 1}</span>
                        {!isReadonly && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (fields.executionLifecycle || []).filter((_, i) => i !== idx);
                              setFields((prev) => ({ ...prev, executionLifecycle: updated }));
                            }}
                            className="text-rose-500 hover:text-rose-700"
                          >
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                      <TextInput
                        label="Phase Name"
                        value={row.phase}
                        disabled={isReadonly}
                        onChange={(e) => {
                          const updated = [...(fields.executionLifecycle || [])];
                          updated[idx] = { ...updated[idx], phase: e.target.value };
                          setFields((prev) => ({ ...prev, executionLifecycle: updated }));
                        }}
                      />
                      <Textarea
                        label="Description"
                        value={row.description}
                        disabled={isReadonly}
                        rows={2}
                        onChange={(e) => {
                          const updated = [...(fields.executionLifecycle || [])];
                          updated[idx] = { ...updated[idx], description: e.target.value };
                          setFields((prev) => ({ ...prev, executionLifecycle: updated }));
                        }}
                      />
                    </div>
                  ))}
                  {!isReadonly && (
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...(fields.executionLifecycle || []), { phase: "New Phase", description: "" }];
                        setFields((prev) => ({ ...prev, executionLifecycle: updated }));
                      }}
                      className="w-full py-1 border border-dashed border-slate-300 rounded text-[11px] font-semibold text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1"
                    >
                      <Plus size={11} /> Add Lifecycle Phase
                    </button>
                  )}
                </div>
              </Section>

              {/* § 4B — Coverage Areas */}
              <Section title="§ 4B · Security Coverage Areas" defaultOpen={false}>
                <p className="text-[10px] text-slate-400 mb-2">Edit bullet points for each coverage discipline (one bullet per line). Leave blank for default template.</p>
                <Textarea
                  label="Web Application Security (one per line)"
                  value={fields.coverageWebText || ""}
                  onChange={(e) => set("coverageWebText", e.target.value)}
                  rows={3}
                  placeholder="Authentication and session management…&#10;Authorisation and access-control enforcement…&#10;OWASP Top 10 vulnerabilities…"
                  disabled={isReadonly}
                />
                <Textarea
                  label="API & Integration Security (one per line)"
                  value={fields.coverageApiText || ""}
                  onChange={(e) => set("coverageApiText", e.target.value)}
                  rows={3}
                  placeholder="Broken object-level authorisation (BOLA)…&#10;Authentication and token enforcement…&#10;OWASP API Security Top 10…"
                  disabled={isReadonly}
                />
                <Textarea
                  label="Payment & Identity Verification Paths (one per line)"
                  value={fields.coveragePaymentText || ""}
                  onChange={(e) => set("coveragePaymentText", e.target.value)}
                  rows={2}
                  placeholder="Integrity and authorisation of the payment workflow…&#10;Security of the identity-verification sandbox path…"
                  disabled={isReadonly}
                />
                <Textarea
                  label="External Network (one per line)"
                  value={fields.coverageNetworkText || ""}
                  onChange={(e) => set("coverageNetworkText", e.target.value)}
                  rows={2}
                  placeholder="Service discovery and enumeration…&#10;Review of exposed services and versions…"
                  disabled={isReadonly}
                />
              </Section>

              {/* § 5 — Timeline & Deliverables */}
              <Section title="§ 5 · Timeline & Deliverables" defaultOpen={true}>
                <div className="grid grid-cols-2 gap-2">
                  <DateInput label="Testing Start" value={fields.testingStartDate} onChange={(v) => set("testingStartDate", v)} disabled={isReadonly} />
                  <DateInput label="Testing End" value={fields.testingEndDate} onChange={(v) => set("testingEndDate", v)} disabled={isReadonly} />
                  <DateInput label="Draft Report" value={fields.draftReportDate} onChange={(v) => set("draftReportDate", v)} disabled={isReadonly} />
                  <DateInput label="Final Report" value={fields.finalReportDate} onChange={(v) => set("finalReportDate", v)} disabled={isReadonly} />
                  <DateInput label="Retesting Ends" value={fields.retestingEndDate} onChange={(v) => set("retestingEndDate", v)} disabled={isReadonly} />
                </div>
                <Textarea
                  label="Timeline Caveats / Assumptions Note"
                  value={fields.timelineAssumptionsText || ""}
                  onChange={(e) => set("timelineAssumptionsText", e.target.value)}
                  rows={2}
                  placeholder="This indicative timeline assumes timely provision of all required target information…"
                  disabled={isReadonly}
                />

                <div className="space-y-2 mt-2">
                  <Label>Deliverables List</Label>
                  {(fields.deliverables || []).map((del, idx) => (
                    <div key={idx} className="p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold text-slate-500 uppercase">Deliverable {idx + 1}</span>
                        {!isReadonly && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (fields.deliverables || []).filter((_, i) => i !== idx);
                              setFields((prev) => ({ ...prev, deliverables: updated }));
                            }}
                            className="text-rose-500 hover:text-rose-700"
                          >
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                      <TextInput
                        label="Deliverable Name"
                        value={del.deliverable}
                        disabled={isReadonly}
                        onChange={(e) => {
                          const updated = [...(fields.deliverables || [])];
                          updated[idx] = { ...updated[idx], deliverable: e.target.value };
                          setFields((prev) => ({ ...prev, deliverables: updated }));
                        }}
                      />
                      <Textarea
                        label="Description"
                        value={del.description}
                        disabled={isReadonly}
                        rows={2}
                        onChange={(e) => {
                          const updated = [...(fields.deliverables || [])];
                          updated[idx] = { ...updated[idx], description: e.target.value };
                          setFields((prev) => ({ ...prev, deliverables: updated }));
                        }}
                      />
                    </div>
                  ))}
                  {!isReadonly && (
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...(fields.deliverables || []), { deliverable: "New Deliverable", description: "" }];
                        setFields((prev) => ({ ...prev, deliverables: updated }));
                      }}
                      className="w-full py-1 border border-dashed border-slate-300 rounded text-[11px] font-semibold text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1"
                    >
                      <Plus size={11} /> Add Deliverable
                    </button>
                  )}
                </div>

                <Textarea
                  label="PTaaS Platform Delivery Description"
                  value={fields.ptaasPlatformText || ""}
                  onChange={(e) => set("ptaasPlatformText", e.target.value)}
                  rows={2}
                  placeholder="The Client receives access to the CTB platform from engagement commencement…"
                  disabled={isReadonly}
                />
              </Section>

              {/* § 6 — Assumptions & Dependencies */}
              <Section title="§ 6 · Assumptions & Dependencies">
                <Textarea
                  label="Assumptions (one per line)"
                  value={fields.assumptions}
                  onChange={(e) => set("assumptions", e.target.value)}
                  rows={3}
                  placeholder="e.g. The Client will provide all required test accounts and allowlisting within 3 business days of signing…"
                  disabled={isReadonly}
                />
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    id="sow-client-deps"
                    checked={fields.clientDependenciesEnabled}
                    onChange={(e) => set("clientDependenciesEnabled", e.target.checked)}
                    disabled={isReadonly}
                    className="rounded border-slate-300 accent-blue-600"
                  />
                  <label htmlFor="sow-client-deps" className="text-[11px] text-slate-600 font-medium cursor-pointer">
                    Include Client Dependencies section
                  </label>
                </div>
                {fields.clientDependenciesEnabled && (
                  <Textarea
                    label="Client Dependencies (one per line)"
                    value={fields.clientDependencies}
                    onChange={(e) => set("clientDependencies", e.target.value)}
                    rows={3}
                    placeholder="e.g. The Client must provision test accounts for each in-scope role at least 5 business days before testing commences…"
                    disabled={isReadonly}
                  />
                )}
              </Section>

              {/* § 7 — Disclaimers & Professional Conduct */}
              <Section title="§ 7 · Disclaimers & Responsibilities" defaultOpen={false}>
                <Textarea
                  label="Disclaimers & Client Responsibilities Text"
                  value={fields.disclaimersText || ""}
                  onChange={(e) => set("disclaimersText", e.target.value)}
                  rows={3}
                  placeholder="Leave blank to use default template wording for professional conduct, risk acknowledgement, and confidentiality…"
                  disabled={isReadonly}
                />
              </Section>

              {/* § 8 — Exclusions */}
              <Section title="§ 8 · Exclusions">
                <Textarea
                  label="Standard Exclusions (leave blank for defaults, one per line)"
                  value={fields.standardExclusions || ""}
                  onChange={(e) => set("standardExclusions", e.target.value)}
                  rows={3}
                  placeholder="Any application, environment, domain not identified…&#10;Denial-of-service and load testing…&#10;Social engineering and phishing…"
                  disabled={isReadonly}
                />
                <Textarea
                  label="Engagement-specific Exclusions (appended to standard list)"
                  value={fields.additionalExclusions}
                  onChange={(e) => set("additionalExclusions", e.target.value)}
                  rows={2}
                  placeholder="e.g. Live payment flows beyond NZD 1.00 transaction cap are excluded…"
                  disabled={isReadonly}
                />
              </Section>

              {/* § 9 — Commercial Terms & Contract Conditions */}
              <Section
                title="§ 9 · Commercial Terms & Contract"
                badge="Auto-filled"
                badgeColor="bg-emerald-50 text-emerald-700 border-emerald-200"
                defaultOpen={true}
              >
                <div className="grid grid-cols-2 gap-2">
                  <SelectInput
                    label="Engagement Model"
                    value={fields.engagementModel}
                    onChange={(v) => set("engagementModel", v as any)}
                    disabled={isReadonly}
                  >
                    <option value="one-time">One-time</option>
                    <option value="recurring">Recurring</option>
                    <option value="retainer">Retainer</option>
                  </SelectInput>
                  <TextInput
                    label="Consultant Days"
                    type="number"
                    min={0}
                    step={0.5}
                    value={fields.consultantDays ?? ""}
                    onChange={(e) =>
                      set("consultantDays", e.target.value === "" ? null : Number(e.target.value))
                    }
                    placeholder="e.g. 5"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Price"
                    type="number"
                    min={0}
                    value={fields.price ?? ""}
                    onChange={(e) =>
                      set("price", e.target.value === "" ? null : Number(e.target.value))
                    }
                    placeholder="e.g. 8500"
                    disabled={isReadonly}
                  />
                  <SelectInput
                    label="Currency"
                    value={fields.currency}
                    onChange={(v) => set("currency", v as "NZD" | "USD")}
                    disabled={isReadonly}
                  >
                    <option value="NZD">NZD</option>
                    <option value="USD">USD</option>
                  </SelectInput>
                </div>
                <Textarea
                  label="Payment Terms"
                  value={fields.paymentTerms}
                  onChange={(e) => {
                    const val = e.target.value;
                    set("paymentTerms", val);
                    if (fields.specificTerms && fields.specificTerms.length > 0) {
                      const updatedSpecific = fields.specificTerms.map((t) =>
                        t.term && /payment/i.test(t.term) ? { ...t, details: val } : t
                      );
                      set("specificTerms", updatedSpecific);
                    }
                  }}
                  rows={2}
                  disabled={isReadonly}
                />
                <div className="grid grid-cols-1 gap-2 mt-1">
                  <TextInput
                    label="Indicative Delivery Duration"
                    value={fields.indicativeDeliveryDuration}
                    onChange={(e) => set("indicativeDeliveryDuration", e.target.value)}
                    placeholder="To be confirmed following scope validation"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Baseline Assessment Duration"
                    value={fields.baselineAssessmentDuration}
                    onChange={(e) => set("baselineAssessmentDuration", e.target.value)}
                    placeholder="To be confirmed at scope validation"
                    disabled={isReadonly}
                  />
                </div>

                {/* Specific Terms Editor */}
                <div className="space-y-2 mt-2 pt-2 border-t border-slate-150">
                  <Label>Specific Terms (Commercial table)</Label>
                  {(fields.specificTerms || []).map((st, idx) => (
                    <div key={idx} className="p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold text-slate-500 uppercase">{st.term}</span>
                        {!isReadonly && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (fields.specificTerms || []).filter((_, i) => i !== idx);
                              setFields((prev) => ({ ...prev, specificTerms: updated }));
                            }}
                            className="text-rose-500 hover:text-rose-700"
                          >
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                      <TextInput
                        label="Term Name"
                        value={st.term}
                        disabled={isReadonly}
                        onChange={(e) => {
                          const updated = [...(fields.specificTerms || [])];
                          updated[idx] = { ...updated[idx], term: e.target.value };
                          setFields((prev) => ({ ...prev, specificTerms: updated }));
                        }}
                      />
                      <Textarea
                        label="Details"
                        value={st.details}
                        disabled={isReadonly}
                        rows={2}
                        onChange={(e) => {
                          const updated = [...(fields.specificTerms || [])];
                          updated[idx] = { ...updated[idx], details: e.target.value };
                          setFields((prev) => ({ ...prev, specificTerms: updated }));
                        }}
                      />
                    </div>
                  ))}
                  {!isReadonly && (
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...(fields.specificTerms || []), { sn: String((fields.specificTerms?.length || 0) + 1), term: "New Term", details: "" }];
                        setFields((prev) => ({ ...prev, specificTerms: updated }));
                      }}
                      className="w-full py-1 border border-dashed border-slate-300 rounded text-[11px] font-semibold text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1"
                    >
                      <Plus size={11} /> Add Specific Term
                    </button>
                  )}
                </div>
              </Section>

              {/* Appendix A — Rules of Engagement */}
              <Section title="Appendix A · Rules of Engagement">
                <Textarea
                  label="RoE Introductory Statement"
                  value={fields.appendixRoEIntro || ""}
                  onChange={(e) => set("appendixRoEIntro", e.target.value)}
                  rows={2}
                  placeholder="This Appendix forms part of this Statement of Work and sets out the operational rules…"
                  disabled={isReadonly}
                />
                <div className="grid grid-cols-1 gap-2 mt-2">
                  <TextInput
                    label="Testing Environment"
                    value={fields.testingEnvironment}
                    onChange={(e) => set("testingEnvironment", e.target.value)}
                    placeholder="Production"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="CTB Testing Source IP / Address"
                    value={fields.testingSourceIp}
                    onChange={(e) => set("testingSourceIp", e.target.value)}
                    placeholder="e.g. 192.0.2.10"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Testing Window"
                    value={fields.testingWindow}
                    onChange={(e) => set("testingWindow", e.target.value)}
                    placeholder="e.g. Mon–Fri 09:00–17:00 NZST"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Blackout Periods"
                    value={fields.blackoutPeriods}
                    onChange={(e) => set("blackoutPeriods", e.target.value)}
                    placeholder="e.g. None"
                    disabled={isReadonly}
                  />
                  <Textarea
                    label="Test Accounts"
                    value={fields.testAccountsNote}
                    onChange={(e) => set("testAccountsNote", e.target.value)}
                    rows={2}
                    placeholder="e.g. Admin: admin@example.com / pass"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Named CTB Testers"
                    value={fields.namedTesters}
                    onChange={(e) => set("namedTesters", e.target.value)}
                    placeholder="e.g. Jane Smith, John Doe"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Standard Access Account"
                    value={fields.standardAccessAccount}
                    onChange={(e) => set("standardAccessAccount", e.target.value)}
                    placeholder="pentest@capturethebug.xyz"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Privileged Access Account"
                    value={fields.privilegedAccessAccount}
                    onChange={(e) => set("privilegedAccessAccount", e.target.value)}
                    placeholder="jk@capturethebug.xyz"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Client Emergency Contact"
                    value={fields.clientEmergencyContact}
                    onChange={(e) => set("clientEmergencyContact", e.target.value)}
                    placeholder="e.g. John Client, +64 21 000 0000"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="CTB Engagement Lead"
                    value={fields.ctbEngagementLead}
                    onChange={(e) => set("ctbEngagementLead", e.target.value)}
                    placeholder="e.g. Jane Smith, +64 21 111 1111"
                    disabled={isReadonly}
                  />
                </div>
                <div className="space-y-2 mt-2 pt-2 border-t border-slate-150">
                  <Textarea
                    label="Prohibited Testing Activities (leave blank for defaults, one per line)"
                    value={fields.prohibitedActivitiesText || ""}
                    onChange={(e) => set("prohibitedActivitiesText", e.target.value)}
                    rows={2}
                    placeholder="Intentionally destroy, encrypt, bulk-download production data…&#10;Modify or delete accounts or records…"
                    disabled={isReadonly}
                  />
                  <Textarea
                    label="Stop-Testing & Emergency Procedure (leave blank for defaults, one per line)"
                    value={fields.stopTestingProcedureText || ""}
                    onChange={(e) => set("stopTestingProcedureText", e.target.value)}
                    rows={2}
                    placeholder="Requesting a pause: either party may request immediate pause…&#10;CTB response: suspend testing immediately…"
                    disabled={isReadonly}
                  />
                </div>
              </Section>

              {/* § 10 — Signature & Billing Block */}
              <Section title="§ 10 · Acceptance, Signature & Billing">
                <p className="text-[10px] text-slate-400 mb-2">Signatory details for the acceptance block</p>
                <div className="grid grid-cols-2 gap-2">
                  <TextInput
                    label="Client Signer Name"
                    value={fields.clientSignerName}
                    onChange={(e) => set("clientSignerName", e.target.value)}
                    placeholder="e.g. Jane Client"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Client Signer Title"
                    value={fields.clientSignerTitle}
                    onChange={(e) => set("clientSignerTitle", e.target.value)}
                    placeholder="e.g. CTO"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Client Phone"
                    value={fields.clientPhone}
                    onChange={(e) => set("clientPhone", e.target.value)}
                    placeholder="e.g. +64 21 000 0000"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Client Email"
                    value={fields.clientEmail}
                    onChange={(e) => set("clientEmail", e.target.value)}
                    placeholder="e.g. cto@client.com"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="CTB Signer Name"
                    value={fields.ctbSignerName}
                    onChange={(e) => set("ctbSignerName", e.target.value)}
                    placeholder="e.g. Ankita Dhakar"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="CTB Signer Title"
                    value={fields.ctbSignerTitle}
                    onChange={(e) => set("ctbSignerTitle", e.target.value)}
                    placeholder="e.g. CEO"
                    disabled={isReadonly}
                  />
                  <DateInput
                    label="Client Signed Date"
                    value={fields.clientSignedAt?.slice(0, 10) || ""}
                    onChange={(v) => set("clientSignedAt", v ? new Date(v).toISOString() : null)}
                    disabled={isReadonly}
                  />
                  <DateInput
                    label="CTB Signed Date"
                    value={fields.ctbSignedAt?.slice(0, 10) || ""}
                    onChange={(v) => set("ctbSignedAt", v ? new Date(v).toISOString() : null)}
                    disabled={isReadonly}
                  />
                </div>
                <p className="text-[10px] font-semibold text-slate-600 mt-3 mb-1">Client Billing Details</p>
                <div className="grid grid-cols-1 gap-2">
                  <TextInput
                    label="Invoice Addressed To"
                    value={fields.clientBillingName}
                    onChange={(e) => set("clientBillingName", e.target.value)}
                    placeholder="e.g. Example Ltd Accounts Payable"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Registered Company Name"
                    value={fields.clientRegisteredCompany}
                    onChange={(e) => set("clientRegisteredCompany", e.target.value)}
                    placeholder="e.g. Example Technologies Limited"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Country of Incorporation"
                    value={fields.clientCountryOfIncorporation}
                    onChange={(e) => set("clientCountryOfIncorporation", e.target.value)}
                    placeholder="e.g. New Zealand"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Billing Address"
                    value={fields.clientBillingAddress}
                    onChange={(e) => set("clientBillingAddress", e.target.value)}
                    placeholder="e.g. 123 Main Street, Auckland 1010"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="GST / Tax / Company Reg Number"
                    value={fields.clientTaxNumber}
                    onChange={(e) => set("clientTaxNumber", e.target.value)}
                    placeholder="e.g. NZ 123-456-789"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Billing Email"
                    value={fields.clientBillingEmail}
                    onChange={(e) => set("clientBillingEmail", e.target.value)}
                    placeholder="e.g. accounts@client.com"
                    disabled={isReadonly}
                  />
                  <TextInput
                    label="Billing Contact Phone"
                    value={fields.clientBillingPhone}
                    onChange={(e) => set("clientBillingPhone", e.target.value)}
                    placeholder="e.g. +64 9 000 0000"
                    disabled={isReadonly}
                  />
                </div>
                <CtbSignaturePad
                  value={fields.ctbSignatureBase64}
                  onChange={(v) => {
                    set("ctbSignatureBase64", v);
                    set("ctbSignedAt", v ? new Date().toISOString() : null);
                  }}
                  disabled={isReadonly}
                />
              </Section>

              {/* Version History */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setHistoryOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition text-left"
                >
                  <span className="flex items-center gap-2">
                    <History size={12} className="text-slate-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-700">
                      Version History
                    </span>
                    {sowBlob?.history?.length ? (
                      <span className="text-[9px] bg-slate-200 text-slate-600 rounded-full px-1.5 font-bold">
                        {sowBlob.history.length}
                      </span>
                    ) : null}
                  </span>
                  {historyOpen ? (
                    <ChevronDown size={13} className="text-slate-400 shrink-0" />
                  ) : (
                    <ChevronRight size={13} className="text-slate-400 shrink-0" />
                  )}
                </button>
                {historyOpen && (
                  <div className="p-3 bg-white">
                    {!sowBlob?.history?.length ? (
                      <p className="text-[10px] text-slate-400">
                        No versions yet — save a draft to start tracking.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {[...(sowBlob.history as SowHistoryEntry[])]
                          .reverse()
                          .map((entry) => {
                            const isSelected = selectedVersionEntry?.version === entry.version;
                            return (
                              <div
                                key={entry.version}
                                className={`flex items-center justify-between text-[10px] text-slate-600 py-1.5 px-2 rounded-lg border transition ${
                                  isSelected ? "bg-amber-50 border-amber-300" : "border-slate-100 hover:bg-slate-50"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-slate-800">
                                    v{entry.version}
                                  </span>
                                  <span className="text-slate-400">·</span>
                                  <span>{entry.savedByUsername || (entry.savedByUserId ? `user ${entry.savedByUserId}` : "—")}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400 text-[9px]">
                                    {new Date(entry.savedAt).toLocaleString()}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedVersionEntry(isSelected ? null : entry)}
                                    className={`px-2 py-0.5 text-[9px] font-bold rounded border transition cursor-pointer ${
                                      isSelected
                                        ? "bg-amber-600 text-white border-amber-600"
                                        : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                                    }`}
                                  >
                                    {isSelected ? "Viewing" : "View"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Bottom padding */}
              <div className="h-4" />
            </div>
          </div>

          {/* RIGHT: PDF Preview */}
          <div className="flex-1 flex flex-col overflow-hidden bg-slate-100">
            {/* Toolbar */}
            <div className="shrink-0 px-4 py-2 border-b border-slate-200 bg-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  PDF Preview
                </span>
                {pdfBuilding && (
                  <span className="flex items-center gap-1 text-[9px] text-blue-600 font-medium">
                    <Loader2 size={10} className="animate-spin" />
                    Rendering…
                  </span>
                )}
                {!pdfBuilding && pdfUrl && (
                  <span className="text-[9px] text-emerald-600 font-medium">
                    ✓ Up to date
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <SalesButton
                  variant="secondary"
                  onClick={() => buildPdf(fields)}
                  disabled={pdfBuilding}
                >
                  <RefreshCw size={10} className={pdfBuilding ? "animate-spin" : ""} />
                  Refresh
                </SalesButton>
                <SalesButton variant="primary" onClick={handleDownload} disabled={pdfBuilding}>
                  <Download size={10} />
                  Download
                </SalesButton>
              </div>
            </div>

            {/* Preview area */}
            <div className="flex-1 overflow-hidden relative">
              {pdfError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-8">
                  <AlertTriangle size={28} className="text-amber-500" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">PDF Render Error</p>
                    <p className="text-xs text-slate-500 mt-1 max-w-xs">{pdfError}</p>
                  </div>
                  <SalesButton variant="secondary" onClick={() => buildPdf(fields)}>
                    <RefreshCw size={11} />
                    Try Again
                  </SalesButton>
                </div>
              ) : pdfBuilding && !pdfUrl ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <Loader2 size={24} className="animate-spin text-blue-500" />
                  <span className="text-xs">Rendering document…</span>
                </div>
              ) : pdfUrl ? (
                <>
                  {/* Overlay spinner when refreshing over existing preview */}
                  {pdfBuilding && (
                    <div className="absolute top-3 right-3 z-10 bg-white/90 rounded-full px-2 py-1 flex items-center gap-1.5 shadow text-[10px] text-slate-500">
                      <Loader2 size={10} className="animate-spin text-blue-500" />
                      Updating…
                    </div>
                  )}
                  <iframe
                    src={pdfUrl}
                    className="w-full h-full border-0"
                    title="SoW PDF Preview"
                  />
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <FileText size={28} className="text-slate-300" />
                  <span className="text-xs">Preview will appear here</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};
